import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import './FacebookAdsPage.css';

const FEED_PREVIEW_CACHE_KEY = 'facebookAdsFeedPreviewCacheV1';
const FEED_PREVIEW_CACHE_TTL_MS = 6 * 60 * 60 * 1000;

function formatDateTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return `${date.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} IST`;
}

function resolveMediaUrl(rawUrl) {
  const url = String(rawUrl || '').trim();
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  if (!url.startsWith('/')) return url;
  const apiBase = String(process.env.REACT_APP_API_BASE_URL || '').trim();
  return apiBase ? `${apiBase}${url}` : url;
}

function withRefreshParam(url, refreshNonce) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  if (!refreshNonce) return raw;
  try {
    const parsed = new URL(raw);
    parsed.searchParams.set('refresh', String(refreshNonce));
    return parsed.toString();
  } catch (_) {
    const hasQuery = raw.includes('?');
    return `${raw}${hasQuery ? '&' : '?'}refresh=${encodeURIComponent(String(refreshNonce))}`;
  }
}

function normalizeLabelValueCounts(input) {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return {};
  const out = {};
  Object.entries(input).forEach(([key, value]) => {
    const label = String(key || '').trim();
    if (!label) return;
    out[label] = Number(value || 0) || 0;
  });
  return out;
}

function readFeedPreviewCache() {
  try {
    const raw = window.localStorage.getItem(FEED_PREVIEW_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const cachedAt = Number(parsed.cachedAt || 0) || 0;
    if (!cachedAt) return null;
    if ((Date.now() - cachedAt) > FEED_PREVIEW_CACHE_TTL_MS) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

function writeFeedPreviewCache(payload = {}) {
  try {
    const toStore = {
      cachedAt: Date.now(),
      completeCatalogPreview: payload.completeCatalogPreview || null,
      googleFeedPreview: payload.googleFeedPreview || null,
      googleReviewFeedPreview: payload.googleReviewFeedPreview || null,
      shopifyBackfillStatus: payload.shopifyBackfillStatus || null,
      autoGenerateStatus: payload.autoGenerateStatus || null,
    };
    window.localStorage.setItem(FEED_PREVIEW_CACHE_KEY, JSON.stringify(toStore));
  } catch (_) {
    // ignore cache write errors
  }
}

function FacebookAdsPage() {
  const { currentUser } = useAuth();
  const [creatives, setCreatives] = useState([]);
  const [creativePagination, setCreativePagination] = useState({ page: 1, limit: 18, totalCount: 0, totalPages: 1 });
  const [creativePage, setCreativePage] = useState(1);
  const [creativeSkuSearchInput, setCreativeSkuSearchInput] = useState('');
  const [creativeSkuSearch, setCreativeSkuSearch] = useState('');
  const [isCreativesOpen, setIsCreativesOpen] = useState(false);
  const [creativesLoading, setCreativesLoading] = useState(false);
  const [creativesLoadedOnce, setCreativesLoadedOnce] = useState(false);
  const [completeCatalogPreview, setCompleteCatalogPreview] = useState({
    feedUrl: '',
    totalItems: 0,
    totalSkusConsidered: 0,
    tokenRequired: false,
    latestUpdatedAt: null,
    skippedNoLink: 0,
  });
  const [googleFeedPreview, setGoogleFeedPreview] = useState({
    feedUrl: '',
    totalItems: 0,
    tokenRequired: false,
    latestUpdatedAt: null,
    label2Count: 0,
    label3Count: 0,
    label4Count: 0,
    label2ValueCounts: {},
    label3ValueCounts: {},
    label4ValueCounts: {},
  });
  const [googleReviewFeedPreview, setGoogleReviewFeedPreview] = useState({
    feedUrl: '',
    totalItems: 0,
    totalProducts: 0,
    tokenRequired: false,
    latestUpdatedAt: null,
  });
  const [loading, setLoading] = useState(true);
  const [feedRefreshing, setFeedRefreshing] = useState({
    complete: false,
    google: false,
    googleReviews: false,
  });
  const [busyGenerate, setBusyGenerate] = useState(false);
  const [busyBatch, setBusyBatch] = useState(false);
  const [busyShopifyBackfill, setBusyShopifyBackfill] = useState(false);
  const [busyShopifyBackfillToggle, setBusyShopifyBackfillToggle] = useState(false);
  const [shopifyBackfillStatus, setShopifyBackfillStatus] = useState({ pendingCount: 0, readyCount: 0, autoEnabled: true, batchLimit: 25 });
  const [busyAutoGenerateToggle, setBusyAutoGenerateToggle] = useState(false);
  const [autoGenerateStatus, setAutoGenerateStatus] = useState({ pendingCount: 0, autoEnabled: true, batchLimit: 10 });
  const [processingCreativeId, setProcessingCreativeId] = useState('');
  const [processingCreativeAction, setProcessingCreativeAction] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [feedImageSettings, setFeedImageSettings] = useState({
    mainImageSource: 'lookbook_editorial',
    additionalImageSources: ['lookbook_editorial', 'ai_creative', 'shopify_image'],
    aiCreativeSize: 'all',
    aiCreativeStrictSize: false,
    updatedAt: null,
    updatedBy: null,
  });
  const [savingFeedImageSettings, setSavingFeedImageSettings] = useState(false);
  const [manualRefreshNonceByFeed, setManualRefreshNonceByFeed] = useState({
    complete: '',
    google: '',
    googleReviews: '',
  });

  const [generateForm, setGenerateForm] = useState({
    entryId: '',
    shopifyProductId: '',
    sizePreset: '1:1',
  });

  const [batchForm, setBatchForm] = useState({
    limit: 20,
    status: 'published',
  });

  const authedFetch = useCallback(async (url, options = {}) => {
    if (!currentUser) throw new Error('User not authenticated.');
    const token = await currentUser.getIdToken();
    const base = String(process.env.REACT_APP_API_BASE_URL || '').trim();
    const resolvedUrl = (base && typeof url === 'string' && url.startsWith('/')) ? `${base}${url}` : url;
    return fetch(resolvedUrl, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(options.headers || {}),
      },
    });
  }, [currentUser]);

  const loadData = useCallback(async ({ forcePreviewRefresh = false } = {}) => {
    setLoading(true);
    setError('');
    try {
      let completeData = null;
      let googleData = null;
      let googleReviewsData = null;
      let backfillStatusData = null;
      let autoGenerateStatusData = null;
      const cachedPreviews = forcePreviewRefresh ? null : readFeedPreviewCache();
      const feedImageSettingsPromise = authedFetch('/api/v1/facebook-ads/feed-image-settings');

      if (
        cachedPreviews?.completeCatalogPreview
        && cachedPreviews?.googleFeedPreview
        && cachedPreviews?.googleReviewFeedPreview
        && cachedPreviews?.shopifyBackfillStatus
        && cachedPreviews?.autoGenerateStatus
        && !forcePreviewRefresh
      ) {
        completeData = cachedPreviews.completeCatalogPreview;
        googleData = cachedPreviews.googleFeedPreview;
        googleReviewsData = cachedPreviews.googleReviewFeedPreview;
        backfillStatusData = cachedPreviews.shopifyBackfillStatus;
        autoGenerateStatusData = cachedPreviews.autoGenerateStatus;
      } else {
        const refreshNonce = forcePreviewRefresh ? String(Date.now()) : '';
        const completeCatalogPreviewUrl = forcePreviewRefresh
          ? `/api/v1/facebook-ads/complete-catalog-feed-preview?refresh=${encodeURIComponent(refreshNonce)}`
          : '/api/v1/facebook-ads/complete-catalog-feed-preview';
        const googlePreviewUrl = forcePreviewRefresh
          ? `/api/v1/google-ads/feed-preview?refresh=${encodeURIComponent(refreshNonce)}`
          : '/api/v1/google-ads/feed-preview';
        const googleReviewsPreviewUrl = forcePreviewRefresh
          ? `/api/v1/google-reviews/feed-preview?refresh=${encodeURIComponent(refreshNonce)}`
          : '/api/v1/google-reviews/feed-preview';
        const [completeResp, googleResp, googleReviewsResp, backfillStatusResp, autoGenerateStatusResp] = await Promise.all([
          authedFetch(completeCatalogPreviewUrl),
          authedFetch(googlePreviewUrl),
          authedFetch(googleReviewsPreviewUrl),
          authedFetch('/api/v1/facebook-ads/backfill-shopify-images/status'),
          authedFetch('/api/v1/facebook-ads/auto-generation/status'),
        ]);
        completeData = await completeResp.json();
        googleData = await googleResp.json();
        googleReviewsData = await googleReviewsResp.json();
        backfillStatusData = await backfillStatusResp.json();
        autoGenerateStatusData = await autoGenerateStatusResp.json();
        if (!completeResp.ok || completeData?.success === false) {
          throw new Error(completeData?.message || 'Failed to load complete catalog feed preview.');
        }
        if (!googleResp.ok || googleData?.success === false) {
          throw new Error(googleData?.message || 'Failed to load Google feed preview.');
        }
        if (!googleReviewsResp.ok || googleReviewsData?.success === false) {
          throw new Error(googleReviewsData?.message || 'Failed to load Google reviews feed preview.');
        }
        if (!backfillStatusResp.ok || backfillStatusData?.success === false) {
          throw new Error(backfillStatusData?.message || 'Failed to load Shopify backfill status.');
        }
        if (!autoGenerateStatusResp.ok || autoGenerateStatusData?.success === false) {
          throw new Error(autoGenerateStatusData?.message || 'Failed to load auto generation status.');
        }
      }

      try {
        const feedImageSettingsResp = await feedImageSettingsPromise;
        const feedImageSettingsData = await feedImageSettingsResp.json();
        if (feedImageSettingsResp.ok && feedImageSettingsData?.success && feedImageSettingsData?.settings) {
          setFeedImageSettings({
            mainImageSource: feedImageSettingsData.settings.mainImageSource || 'lookbook_editorial',
            additionalImageSources: Array.isArray(feedImageSettingsData.settings.additionalImageSources)
              ? feedImageSettingsData.settings.additionalImageSources
              : ['lookbook_editorial', 'ai_creative', 'shopify_image'],
            aiCreativeSize: feedImageSettingsData.settings.aiCreativeSize || 'all',
            aiCreativeStrictSize: Boolean(feedImageSettingsData.settings.aiCreativeStrictSize),
            updatedAt: feedImageSettingsData.settings.updatedAt || null,
            updatedBy: feedImageSettingsData.settings.updatedBy || null,
          });
        }
      } catch (settingsErr) {
        console.warn('Failed to load feed image settings:', settingsErr);
      }
      setCompleteCatalogPreview({
        feedUrl: String(completeData?.feedUrl || '').trim(),
        totalItems: Number(completeData?.totalItems || 0) || 0,
        totalSkusConsidered: Number(completeData?.totalSkusConsidered || 0) || 0,
        tokenRequired: completeData?.tokenRequired === true,
        latestUpdatedAt: completeData?.latestUpdatedAt || null,
        skippedNoLink: Number(completeData?.skippedNoLink || 0) || 0,
      });
      setGoogleFeedPreview({
        feedUrl: String(googleData?.feedUrl || '').trim(),
        totalItems: Number(googleData?.totalItems || 0) || 0,
        tokenRequired: googleData?.tokenRequired === true,
        latestUpdatedAt: googleData?.latestUpdatedAt || null,
        label2Count: Number(googleData?.label2Count || 0) || 0,
        label3Count: Number(googleData?.label3Count || 0) || 0,
        label4Count: Number(googleData?.label4Count || 0) || 0,
        label2ValueCounts: normalizeLabelValueCounts(googleData?.label2ValueCounts),
        label3ValueCounts: normalizeLabelValueCounts(googleData?.label3ValueCounts),
        label4ValueCounts: normalizeLabelValueCounts(googleData?.label4ValueCounts),
      });
      setGoogleReviewFeedPreview({
        feedUrl: String(googleReviewsData?.feedUrl || '').trim(),
        totalItems: Number(googleReviewsData?.totalItems || 0) || 0,
        totalProducts: Number(googleReviewsData?.totalProducts || 0) || 0,
        tokenRequired: googleReviewsData?.tokenRequired === true,
        latestUpdatedAt: googleReviewsData?.latestUpdatedAt || null,
      });
      setShopifyBackfillStatus({
        pendingCount: Number(backfillStatusData?.pendingCount || 0) || 0,
        readyCount: Number(backfillStatusData?.readyCount || 0) || 0,
        autoEnabled: backfillStatusData?.autoEnabled !== false,
        batchLimit: Number(backfillStatusData?.batchLimit || 25) || 25,
      });
      setAutoGenerateStatus({
        pendingCount: Number(autoGenerateStatusData?.pendingCount || 0) || 0,
        autoEnabled: autoGenerateStatusData?.autoEnabled !== false,
        batchLimit: Number(autoGenerateStatusData?.batchLimit || 10) || 10,
      });
      writeFeedPreviewCache({
        completeCatalogPreview: {
          feedUrl: String(completeData?.feedUrl || '').trim(),
          totalItems: Number(completeData?.totalItems || 0) || 0,
          totalSkusConsidered: Number(completeData?.totalSkusConsidered || 0) || 0,
          tokenRequired: completeData?.tokenRequired === true,
          latestUpdatedAt: completeData?.latestUpdatedAt || null,
          skippedNoLink: Number(completeData?.skippedNoLink || 0) || 0,
        },
        googleFeedPreview: {
          feedUrl: String(googleData?.feedUrl || '').trim(),
          totalItems: Number(googleData?.totalItems || 0) || 0,
          tokenRequired: googleData?.tokenRequired === true,
          latestUpdatedAt: googleData?.latestUpdatedAt || null,
          label2Count: Number(googleData?.label2Count || 0) || 0,
          label3Count: Number(googleData?.label3Count || 0) || 0,
          label4Count: Number(googleData?.label4Count || 0) || 0,
          label2ValueCounts: normalizeLabelValueCounts(googleData?.label2ValueCounts),
          label3ValueCounts: normalizeLabelValueCounts(googleData?.label3ValueCounts),
          label4ValueCounts: normalizeLabelValueCounts(googleData?.label4ValueCounts),
        },
        googleReviewFeedPreview: {
          feedUrl: String(googleReviewsData?.feedUrl || '').trim(),
          totalItems: Number(googleReviewsData?.totalItems || 0) || 0,
          totalProducts: Number(googleReviewsData?.totalProducts || 0) || 0,
          tokenRequired: googleReviewsData?.tokenRequired === true,
          latestUpdatedAt: googleReviewsData?.latestUpdatedAt || null,
        },
        shopifyBackfillStatus: {
          pendingCount: Number(backfillStatusData?.pendingCount || 0) || 0,
          readyCount: Number(backfillStatusData?.readyCount || 0) || 0,
          autoEnabled: backfillStatusData?.autoEnabled !== false,
          batchLimit: Number(backfillStatusData?.batchLimit || 25) || 25,
        },
        autoGenerateStatus: {
          pendingCount: Number(autoGenerateStatusData?.pendingCount || 0) || 0,
          autoEnabled: autoGenerateStatusData?.autoEnabled !== false,
          batchLimit: Number(autoGenerateStatusData?.batchLimit || 10) || 10,
        },
      });
    } catch (err) {
      setError(err.message || 'Failed to load Facebook ad data.');
    } finally {
      setLoading(false);
    }
  }, [authedFetch]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const fetchCreatives = useCallback(async (page = 1, sku = '') => {
    setCreativesLoading(true);
    try {
      const creativesUrl = new URL('/api/v1/facebook-ads/creatives', 'http://local');
      creativesUrl.searchParams.set('limit', '18');
      creativesUrl.searchParams.set('page', String(page));
      if (String(sku || '').trim()) creativesUrl.searchParams.set('sku', String(sku || '').trim());
      const resp = await authedFetch(`${creativesUrl.pathname}${creativesUrl.search}`);
      const data = await resp.json();
      if (!resp.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to load Facebook ad creatives.');
      }
      setCreatives(Array.isArray(data?.creatives) ? data.creatives : []);
      const nextPagination = data?.pagination && typeof data.pagination === 'object'
        ? data.pagination
        : {};
      const safePage = Number(nextPagination.page || 1) || 1;
      const safeTotalPages = Number(nextPagination.totalPages || 1) || 1;
      setCreativePagination({
        page: safePage,
        limit: Number(nextPagination.limit || 18) || 18,
        totalCount: Number(nextPagination.totalCount || 0) || 0,
        totalPages: safeTotalPages,
      });
      setCreativePage(safePage);
      setCreativesLoadedOnce(true);
    } catch (err) {
      console.error('Failed to load creatives:', err);
      setError(err.message || 'Failed to load Facebook ad creatives.');
    } finally {
      setCreativesLoading(false);
    }
  }, [authedFetch]);

  const handleToggleCreativesAccordion = useCallback(() => {
    setIsCreativesOpen((prev) => {
      const next = !prev;
      if (next && !creativesLoadedOnce) {
        fetchCreatives(creativePage, creativeSkuSearch);
      }
      return next;
    });
  }, [creativesLoadedOnce, creativePage, creativeSkuSearch, fetchCreatives]);

  const handleCreativePageChange = useCallback((newPage) => {
    setCreativePage(newPage);
    fetchCreatives(newPage, creativeSkuSearch);
  }, [creativeSkuSearch, fetchCreatives]);

  const handleApplyCreativeSkuSearch = useCallback(() => {
    const normalized = String(creativeSkuSearchInput || '').trim().toUpperCase();
    setCreativeSkuSearch(normalized);
    setCreativePage(1);
    fetchCreatives(1, normalized);
  }, [creativeSkuSearchInput, fetchCreatives]);

  const handleClearCreativeSkuSearch = useCallback(() => {
    setCreativeSkuSearchInput('');
    setCreativeSkuSearch('');
    setCreativePage(1);
    fetchCreatives(1, '');
  }, [fetchCreatives]);

  const refreshSingleFeed = useCallback(async (feedKey) => {
    const key = String(feedKey || '').trim();
    if (!['complete', 'google', 'googleReviews'].includes(key)) return;
    setFeedRefreshing((prev) => ({ ...prev, [key]: true }));
    setError('');
    setMessage('');
    try {
      const refreshNonce = String(Date.now());
      if (key === 'complete') {
        const resp = await authedFetch(`/api/v1/facebook-ads/complete-catalog-feed-preview?refresh=${encodeURIComponent(refreshNonce)}`);
        const data = await resp.json();
        if (!resp.ok || data?.success === false) throw new Error(data?.message || 'Failed to refresh complete catalog feed.');
        setCompleteCatalogPreview({
          feedUrl: String(data?.feedUrl || '').trim(),
          totalItems: Number(data?.totalItems || 0) || 0,
          totalSkusConsidered: Number(data?.totalSkusConsidered || 0) || 0,
          tokenRequired: data?.tokenRequired === true,
          latestUpdatedAt: data?.latestUpdatedAt || null,
          skippedNoLink: Number(data?.skippedNoLink || 0) || 0,
        });
        setManualRefreshNonceByFeed((prev) => ({ ...prev, complete: refreshNonce }));
        writeFeedPreviewCache({
          completeCatalogPreview: {
            feedUrl: String(data?.feedUrl || '').trim(),
            totalItems: Number(data?.totalItems || 0) || 0,
            totalSkusConsidered: Number(data?.totalSkusConsidered || 0) || 0,
            tokenRequired: data?.tokenRequired === true,
            latestUpdatedAt: data?.latestUpdatedAt || null,
            skippedNoLink: Number(data?.skippedNoLink || 0) || 0,
          },
          googleFeedPreview,
          googleReviewFeedPreview,
        });
      } else if (key === 'google') {
        const resp = await authedFetch(`/api/v1/google-ads/feed-preview?refresh=${encodeURIComponent(refreshNonce)}`);
        const data = await resp.json();
        if (!resp.ok || data?.success === false) throw new Error(data?.message || 'Failed to refresh Google feed.');
        setGoogleFeedPreview({
          feedUrl: String(data?.feedUrl || '').trim(),
          totalItems: Number(data?.totalItems || 0) || 0,
          tokenRequired: data?.tokenRequired === true,
          latestUpdatedAt: data?.latestUpdatedAt || null,
          label2Count: Number(data?.label2Count || 0) || 0,
          label3Count: Number(data?.label3Count || 0) || 0,
          label4Count: Number(data?.label4Count || 0) || 0,
          label2ValueCounts: normalizeLabelValueCounts(data?.label2ValueCounts),
          label3ValueCounts: normalizeLabelValueCounts(data?.label3ValueCounts),
          label4ValueCounts: normalizeLabelValueCounts(data?.label4ValueCounts),
        });
        setManualRefreshNonceByFeed((prev) => ({ ...prev, google: refreshNonce }));
        writeFeedPreviewCache({
          completeCatalogPreview,
          googleFeedPreview: {
            feedUrl: String(data?.feedUrl || '').trim(),
            totalItems: Number(data?.totalItems || 0) || 0,
            tokenRequired: data?.tokenRequired === true,
            latestUpdatedAt: data?.latestUpdatedAt || null,
            label2Count: Number(data?.label2Count || 0) || 0,
            label3Count: Number(data?.label3Count || 0) || 0,
            label4Count: Number(data?.label4Count || 0) || 0,
            label2ValueCounts: normalizeLabelValueCounts(data?.label2ValueCounts),
            label3ValueCounts: normalizeLabelValueCounts(data?.label3ValueCounts),
            label4ValueCounts: normalizeLabelValueCounts(data?.label4ValueCounts),
          },
          googleReviewFeedPreview,
        });
      } else if (key === 'googleReviews') {
        const resp = await authedFetch(`/api/v1/google-reviews/feed-preview?refresh=${encodeURIComponent(refreshNonce)}`);
        const data = await resp.json();
        if (!resp.ok || data?.success === false) throw new Error(data?.message || 'Failed to refresh Google reviews feed.');
        setGoogleReviewFeedPreview({
          feedUrl: String(data?.feedUrl || '').trim(),
          totalItems: Number(data?.totalItems || 0) || 0,
          totalProducts: Number(data?.totalProducts || 0) || 0,
          tokenRequired: data?.tokenRequired === true,
          latestUpdatedAt: data?.latestUpdatedAt || null,
        });
        setManualRefreshNonceByFeed((prev) => ({ ...prev, googleReviews: refreshNonce }));
        writeFeedPreviewCache({
          completeCatalogPreview,
          googleFeedPreview,
          googleReviewFeedPreview: {
            feedUrl: String(data?.feedUrl || '').trim(),
            totalItems: Number(data?.totalItems || 0) || 0,
            totalProducts: Number(data?.totalProducts || 0) || 0,
            tokenRequired: data?.tokenRequired === true,
            latestUpdatedAt: data?.latestUpdatedAt || null,
          },
        });
      }
      setMessage('Selected feed refreshed.');
    } catch (err) {
      setError(err.message || 'Failed to refresh selected feed.');
    } finally {
      setFeedRefreshing((prev) => ({ ...prev, [key]: false }));
    }
  }, [authedFetch, completeCatalogPreview, googleFeedPreview, googleReviewFeedPreview]);

  const summary = useMemo(() => {
    const total = creativePagination.totalCount || shopifyBackfillStatus.readyCount || creatives.length || 0;
    const ready = shopifyBackfillStatus.readyCount || creatives.filter((item) => String(item?.status || '').toLowerCase() === 'ready').length;
    const presets = new Set(creatives.map((item) => String(item?.sizePreset || '').trim()).filter(Boolean));
    return {
      total,
      ready,
      presets: presets.size > 0 ? presets.size : (creativesLoadedOnce ? 0 : '3 sizes'),
    };
  }, [creatives, creativePagination.totalCount, shopifyBackfillStatus.readyCount, creativesLoadedOnce]);

  const handleGenerateOne = useCallback(async (event) => {
    event.preventDefault();
    setBusyGenerate(true);
    setError('');
    setMessage('');
    try {
      const payload = {
        entryId: String(generateForm.entryId || '').trim() || undefined,
        shopifyProductId: String(generateForm.shopifyProductId || '').trim() || undefined,
        template: 'split_70_30',
        sizePreset: generateForm.sizePreset,
      };
      if (!payload.entryId && !payload.shopifyProductId) {
        throw new Error('Enter entryId or productId to generate a creative.');
      }

      const resp = await authedFetch('/api/v1/facebook-ads/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await resp.json();
      if (!resp.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to generate Facebook ad creative.');
      }
      setMessage(data?.message || 'Creative generated.');
      await loadData();
      if (isCreativesOpen || creativesLoadedOnce) {
        await fetchCreatives(creativePage, creativeSkuSearch);
      }
    } catch (err) {
      setError(err.message || 'Failed to generate creative.');
    } finally {
      setBusyGenerate(false);
    }
  }, [authedFetch, generateForm, loadData, isCreativesOpen, creativesLoadedOnce, creativePage, creativeSkuSearch, fetchCreatives]);

  const handleGenerateBatch = useCallback(async (event) => {
    event.preventDefault();
    setBusyBatch(true);
    setError('');
    setMessage('');
    try {
      const resp = await authedFetch('/api/v1/facebook-ads/generate-batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          limit: Number(batchForm.limit) || 20,
          status: String(batchForm.status || '').trim() || undefined,
        }),
      });
      const data = await resp.json();
      if (!resp.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to run batch generation.');
      }
      setMessage(data?.message || 'Batch generation finished.');
      await loadData();
      if (isCreativesOpen || creativesLoadedOnce) {
        await fetchCreatives(creativePage, creativeSkuSearch);
      }
    } catch (err) {
      setError(err.message || 'Failed to run batch generation.');
    } finally {
      setBusyBatch(false);
    }
  }, [authedFetch, batchForm, loadData, isCreativesOpen, creativesLoadedOnce, creativePage, creativeSkuSearch, fetchCreatives]);

  const handleRegenerateCreative = useCallback(async (creative) => {
    const creativeId = String(creative?.id || '').trim();
    if (!creativeId) return;
    setProcessingCreativeId(creativeId);
    setProcessingCreativeAction('regenerate');
    setError('');
    setMessage('');
    try {
      const payload = {
        entryId: String(creative?.lookbookEntryId || '').trim() || undefined,
        shopifyProductId: String(creative?.shopifyProductId || '').trim() || undefined,
        template: String(creative?.template || '').trim() || 'split_70_30',
        sizePreset: String(creative?.sizePreset || '').trim() || '1:1',
      };
      if (!payload.entryId && !payload.shopifyProductId) {
        throw new Error('Cannot regenerate: missing source entry or product id.');
      }

      const resp = await authedFetch('/api/v1/facebook-ads/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await resp.json();
      if (!resp.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to regenerate creative.');
      }
      setMessage(`Creative regenerated for product ${payload.shopifyProductId || '-'}.`);
      await loadData();
      if (isCreativesOpen || creativesLoadedOnce) {
        await fetchCreatives(creativePage, creativeSkuSearch);
      }
    } catch (err) {
      setError(err.message || 'Failed to regenerate creative.');
    } finally {
      setProcessingCreativeId('');
      setProcessingCreativeAction('');
    }
  }, [authedFetch, loadData, isCreativesOpen, creativesLoadedOnce, creativePage, creativeSkuSearch, fetchCreatives]);

  const handleToggleShopifyBackfillAuto = useCallback(async () => {
    setBusyShopifyBackfillToggle(true);
    setError('');
    setMessage('');
    try {
      const nextEnabled = shopifyBackfillStatus.autoEnabled !== true;
      const resp = await authedFetch('/api/v1/facebook-ads/backfill-shopify-images/settings', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          enabled: nextEnabled,
          batchLimit: 25,
        }),
      });
      const data = await resp.json();
      if (!resp.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to update auto backfill setting.');
      }
      setShopifyBackfillStatus((prev) => ({
        ...prev,
        autoEnabled: data?.settings?.enabled === true,
        batchLimit: Number(data?.settings?.batchLimit || 25) || 25,
      }));
      setMessage(data?.message || `Auto backfill ${nextEnabled ? 'started' : 'paused'}.`);
    } catch (err) {
      setError(err.message || 'Failed to update auto backfill setting.');
    } finally {
      setBusyShopifyBackfillToggle(false);
    }
  }, [authedFetch, shopifyBackfillStatus.autoEnabled]);

  const handleToggleAutoGenerate = useCallback(async () => {
    setBusyAutoGenerateToggle(true);
    setError('');
    setMessage('');
    try {
      const nextEnabled = autoGenerateStatus.autoEnabled !== true;
      const resp = await authedFetch('/api/v1/facebook-ads/auto-generation/settings', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          enabled: nextEnabled,
          batchLimit: Number(autoGenerateStatus.batchLimit || 10) || 10,
        }),
      });
      const data = await resp.json();
      if (!resp.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to update auto generation setting.');
      }
      setAutoGenerateStatus((prev) => ({
        ...prev,
        autoEnabled: data?.settings?.enabled === true,
        batchLimit: Number(data?.settings?.batchLimit || prev.batchLimit || 10) || 10,
      }));
      setMessage(data?.message || `Auto generation ${nextEnabled ? 'started' : 'paused'}.`);
    } catch (err) {
      setError(err.message || 'Failed to update auto generation setting.');
    } finally {
      setBusyAutoGenerateToggle(false);
    }
  }, [authedFetch, autoGenerateStatus.autoEnabled, autoGenerateStatus.batchLimit]);

  const handleDeleteCreative = useCallback(async (creative) => {
    const creativeId = String(creative?.id || '').trim();
    if (!creativeId) return;
    const shouldDelete = window.confirm('Delete this creative? This removes it from feed output.');
    if (!shouldDelete) return;
    setProcessingCreativeId(creativeId);
    setProcessingCreativeAction('delete');
    setError('');
    setMessage('');
    try {
      const resp = await authedFetch(`/api/v1/facebook-ads/creatives/${encodeURIComponent(creativeId)}`, {
        method: 'DELETE',
      });
      const data = await resp.json();
      if (!resp.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to delete creative.');
      }
      setMessage(`Creative deleted for product ${creative?.shopifyProductId || '-'}.`);
      await loadData();
      if (isCreativesOpen || creativesLoadedOnce) {
        await fetchCreatives(creativePage, creativeSkuSearch);
      }
    } catch (err) {
      setError(err.message || 'Failed to delete creative.');
    } finally {
      setProcessingCreativeId('');
      setProcessingCreativeAction('');
    }
  }, [authedFetch, loadData, isCreativesOpen, creativesLoadedOnce, creativePage, creativeSkuSearch, fetchCreatives]);

  const handleBackfillShopifyImages = useCallback(async () => {
    setBusyShopifyBackfill(true);
    setError('');
    setMessage('');
    try {
      const resp = await authedFetch('/api/v1/facebook-ads/backfill-shopify-images', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ limit: 5, dryRun: false }),
      });
      const data = await resp.json();
      if (!resp.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to backfill Shopify image URLs.');
      }
      setMessage(data?.message || 'Shopify image backfill completed.');
      await loadData({ forcePreviewRefresh: true });
    } catch (err) {
      setError(err.message || 'Failed to run Shopify image backfill.');
    } finally {
      setBusyShopifyBackfill(false);
    }
  }, [authedFetch, loadData]);

  const handleToggleAdditionalSource = useCallback((sourceKey) => {
    setFeedImageSettings((prev) => {
      const current = Array.isArray(prev.additionalImageSources) ? prev.additionalImageSources : [];
      const exists = current.includes(sourceKey);
      const next = exists ? current.filter((k) => k !== sourceKey) : [...current, sourceKey];
      return { ...prev, additionalImageSources: next };
    });
  }, []);

  const handleSaveFeedImageSettings = useCallback(async () => {
    setSavingFeedImageSettings(true);
    setError('');
    setMessage('');
    try {
      const resp = await authedFetch('/api/v1/facebook-ads/feed-image-settings', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mainImageSource: feedImageSettings.mainImageSource,
          additionalImageSources: feedImageSettings.additionalImageSources,
          aiCreativeSize: feedImageSettings.aiCreativeSize || 'all',
          aiCreativeStrictSize: Boolean(feedImageSettings.aiCreativeStrictSize),
        }),
      });
      const data = await resp.json();
      if (!resp.ok || data?.success === false) {
        throw new Error(data?.message || 'Failed to save feed image settings.');
      }
      if (data?.settings) {
        setFeedImageSettings({
          mainImageSource: data.settings.mainImageSource || feedImageSettings.mainImageSource,
          additionalImageSources: Array.isArray(data.settings.additionalImageSources)
            ? data.settings.additionalImageSources
            : feedImageSettings.additionalImageSources,
          aiCreativeSize: data.settings.aiCreativeSize || feedImageSettings.aiCreativeSize || 'all',
          aiCreativeStrictSize: typeof data.settings.aiCreativeStrictSize !== 'undefined'
            ? Boolean(data.settings.aiCreativeStrictSize)
            : Boolean(feedImageSettings.aiCreativeStrictSize),
          updatedAt: data.settings.updatedAt || null,
          updatedBy: data.settings.updatedBy || null,
        });
      }
      setMessage(data?.message || 'Feed image settings saved successfully. Click Manual Refresh on the feeds below to regenerate them.');
    } catch (err) {
      setError(err.message || 'Failed to save feed image settings.');
    } finally {
      setSavingFeedImageSettings(false);
    }
  }, [authedFetch, feedImageSettings]);

  const handleRefreshAll = useCallback(async () => {
    await loadData({ forcePreviewRefresh: true });
    if (isCreativesOpen || creativesLoadedOnce) {
      await fetchCreatives(creativePage, creativeSkuSearch);
    }
  }, [loadData, isCreativesOpen, creativesLoadedOnce, creativePage, creativeSkuSearch, fetchCreatives]);

  return (
    <div className="facebook-ads-admin">
      <header className="facebook-ads-admin__header">
        <div>
          <h1>Facebook Ads Creative</h1>
          <p>Generate ad-specific composite images from lookbook output + fabric source, and publish catalog feed URLs.</p>
        </div>
        <button type="button" className="facebook-ads-admin__refresh" onClick={handleRefreshAll} disabled={loading || creativesLoading}>
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </header>

      {error && <div className="facebook-ads-admin__banner is-error">{error}</div>}
      {message && <div className="facebook-ads-admin__banner is-success">{message}</div>}

      <section className="facebook-ads-admin__summary">
        <article className="facebook-ads-admin__summary-card">
          <span>Total Creatives</span>
          <strong>{summary.total}</strong>
        </article>
        <article className="facebook-ads-admin__summary-card">
          <span>Ready Creatives</span>
          <strong>{summary.ready}</strong>
        </article>
        <article className="facebook-ads-admin__summary-card">
          <span>Size Presets Used</span>
          <strong>{summary.presets}</strong>
        </article>
      </section>

      <section className="facebook-ads-admin__panel">
        <div className="facebook-ads-admin__panel-header">
          <div>
            <h2>Feed Image Selection Settings</h2>
            <p className="facebook-ads-admin__panel-subtitle">
              Configure which images appear as the main image and gallery images in Facebook Complete Catalog &amp; Google Ads feeds.
            </p>
          </div>
          <button
            type="button"
            className="facebook-ads-admin__refresh"
            onClick={handleSaveFeedImageSettings}
            disabled={savingFeedImageSettings || loading}
          >
            {savingFeedImageSettings ? 'Saving...' : 'Save Image Settings'}
          </button>
        </div>

        <div className="facebook-ads-admin__settings-grid">
          {/* Main Image Source */}
          <div className="facebook-ads-admin__setting-group">
            <div className="facebook-ads-admin__setting-title">Main Image Priority (image_link)</div>
            <div className="facebook-ads-admin__setting-desc">
              Select which image source to prioritize for the primary product image. If the preferred source has no image for a product, the system automatically falls back to secondary sources.
            </div>

            <label
              className={`facebook-ads-admin__radio-card ${feedImageSettings.mainImageSource === 'lookbook_editorial' ? 'is-selected' : ''}`}
            >
              <input
                type="radio"
                name="mainImageSource"
                value="lookbook_editorial"
                checked={feedImageSettings.mainImageSource === 'lookbook_editorial'}
                onChange={() => setFeedImageSettings((prev) => ({ ...prev, mainImageSource: 'lookbook_editorial' }))}
              />
              <div className="facebook-ads-admin__card-content">
                <span className="facebook-ads-admin__card-label">Lookbook / Editorial Photoshoot</span>
                <span className="facebook-ads-admin__card-sublabel">Priority: Lookbook Editorial &rarr; AI Creative &rarr; Shopify Primary</span>
              </div>
            </label>

            <label
              className={`facebook-ads-admin__radio-card ${feedImageSettings.mainImageSource === 'ai_creative' ? 'is-selected' : ''}`}
            >
              <input
                type="radio"
                name="mainImageSource"
                value="ai_creative"
                checked={feedImageSettings.mainImageSource === 'ai_creative'}
                onChange={() => setFeedImageSettings((prev) => ({ ...prev, mainImageSource: 'ai_creative' }))}
              />
              <div className="facebook-ads-admin__card-content">
                <span className="facebook-ads-admin__card-label">AI Generated Creative (Composite Ad)</span>
                <span className="facebook-ads-admin__card-sublabel">Priority: AI Creative &rarr; Lookbook Editorial &rarr; Shopify Primary</span>
              </div>
            </label>

            <label
              className={`facebook-ads-admin__radio-card ${feedImageSettings.mainImageSource === 'shopify_image' ? 'is-selected' : ''}`}
            >
              <input
                type="radio"
                name="mainImageSource"
                value="shopify_image"
                checked={feedImageSettings.mainImageSource === 'shopify_image'}
                onChange={() => setFeedImageSettings((prev) => ({ ...prev, mainImageSource: 'shopify_image' }))}
              />
              <div className="facebook-ads-admin__card-content">
                <span className="facebook-ads-admin__card-label">Shopify Main Image (Default Store Image)</span>
                <span className="facebook-ads-admin__card-sublabel">Priority: Shopify Primary &rarr; Lookbook Editorial &rarr; AI Creative</span>
              </div>
            </label>
          </div>

          {/* Additional Images Sources */}
          <div className="facebook-ads-admin__setting-group">
            <div className="facebook-ads-admin__setting-title">Additional Images (additional_image_link)</div>
            <div className="facebook-ads-admin__setting-desc">
              Select which image sources are included in the feed gallery (up to 10 images). Any image chosen as the primary image is automatically excluded to prevent duplicates.
            </div>

            <label
              className={`facebook-ads-admin__checkbox-card ${feedImageSettings.additionalImageSources?.includes('lookbook_editorial') ? 'is-selected' : ''}`}
            >
              <input
                type="checkbox"
                checked={feedImageSettings.additionalImageSources?.includes('lookbook_editorial')}
                onChange={() => handleToggleAdditionalSource('lookbook_editorial')}
              />
              <div className="facebook-ads-admin__card-content">
                <span className="facebook-ads-admin__card-label">Lookbook Editorial Images</span>
                <span className="facebook-ads-admin__card-sublabel">Model photoshoot and flatlay images from lookbook</span>
              </div>
            </label>

            <label
              className={`facebook-ads-admin__checkbox-card ${feedImageSettings.additionalImageSources?.includes('ai_creative') ? 'is-selected' : ''}`}
            >
              <input
                type="checkbox"
                checked={feedImageSettings.additionalImageSources?.includes('ai_creative')}
                onChange={() => handleToggleAdditionalSource('ai_creative')}
              />
              <div className="facebook-ads-admin__card-content">
                <span className="facebook-ads-admin__card-label">AI Ad Creatives</span>
                <span className="facebook-ads-admin__card-sublabel">Generated composite 1:1, 4:5 and 9:16 ad creative images</span>
              </div>
            </label>

            <label
              className={`facebook-ads-admin__checkbox-card ${feedImageSettings.additionalImageSources?.includes('shopify_image') ? 'is-selected' : ''}`}
            >
              <input
                type="checkbox"
                checked={feedImageSettings.additionalImageSources?.includes('shopify_image')}
                onChange={() => handleToggleAdditionalSource('shopify_image')}
              />
              <div className="facebook-ads-admin__card-content">
                <span className="facebook-ads-admin__card-label">Shopify Gallery Images</span>
                <span className="facebook-ads-admin__card-sublabel">Remaining product photos from your Shopify catalog gallery</span>
              </div>
            </label>
          </div>

          {/* AI Creative Aspect Ratio / Size */}
          <div className="facebook-ads-admin__setting-group">
            <div className="facebook-ads-admin__setting-title">AI Creative Aspect Ratio / Size</div>
            <div className="facebook-ads-admin__setting-desc">
              Select preferred aspect ratio when AI creatives are used in feeds. If strict matching is off, other sizes are used as fallbacks.
            </div>

            <label
              className={`facebook-ads-admin__radio-card ${feedImageSettings.aiCreativeSize === 'all' ? 'is-selected' : ''}`}
            >
              <input
                type="radio"
                name="aiCreativeSize"
                value="all"
                checked={feedImageSettings.aiCreativeSize === 'all'}
                onChange={() => setFeedImageSettings((prev) => ({ ...prev, aiCreativeSize: 'all' }))}
              />
              <div className="facebook-ads-admin__card-content">
                <span className="facebook-ads-admin__card-label">All / Any Size (Default)</span>
                <span className="facebook-ads-admin__card-sublabel">Priority: 1:1 Square &rarr; 4:5 Portrait &rarr; 9:16 Story</span>
              </div>
            </label>

            <label
              className={`facebook-ads-admin__radio-card ${feedImageSettings.aiCreativeSize === '1:1' ? 'is-selected' : ''}`}
            >
              <input
                type="radio"
                name="aiCreativeSize"
                value="1:1"
                checked={feedImageSettings.aiCreativeSize === '1:1'}
                onChange={() => setFeedImageSettings((prev) => ({ ...prev, aiCreativeSize: '1:1' }))}
              />
              <div className="facebook-ads-admin__card-content">
                <span className="facebook-ads-admin__card-label">1:1 Square</span>
                <span className="facebook-ads-admin__card-sublabel">Priority: 1:1 &rarr; 4:5 &rarr; 9:16</span>
              </div>
            </label>

            <label
              className={`facebook-ads-admin__radio-card ${feedImageSettings.aiCreativeSize === '4:5' ? 'is-selected' : ''}`}
            >
              <input
                type="radio"
                name="aiCreativeSize"
                value="4:5"
                checked={feedImageSettings.aiCreativeSize === '4:5'}
                onChange={() => setFeedImageSettings((prev) => ({ ...prev, aiCreativeSize: '4:5' }))}
              />
              <div className="facebook-ads-admin__card-content">
                <span className="facebook-ads-admin__card-label">4:5 Portrait / Feed</span>
                <span className="facebook-ads-admin__card-sublabel">Priority: 4:5 &rarr; 1:1 &rarr; 9:16</span>
              </div>
            </label>

            <label
              className={`facebook-ads-admin__radio-card ${feedImageSettings.aiCreativeSize === '9:16' ? 'is-selected' : ''}`}
            >
              <input
                type="radio"
                name="aiCreativeSize"
                value="9:16"
                checked={feedImageSettings.aiCreativeSize === '9:16'}
                onChange={() => setFeedImageSettings((prev) => ({ ...prev, aiCreativeSize: '9:16' }))}
              />
              <div className="facebook-ads-admin__card-content">
                <span className="facebook-ads-admin__card-label">9:16 Story / Reel</span>
                <span className="facebook-ads-admin__card-sublabel">Priority: 9:16 &rarr; 4:5 &rarr; 1:1</span>
              </div>
            </label>

            <label
              className={`facebook-ads-admin__checkbox-card facebook-ads-admin__strict-toggle ${feedImageSettings.aiCreativeStrictSize && feedImageSettings.aiCreativeSize !== 'all' ? 'is-selected' : ''} ${feedImageSettings.aiCreativeSize === 'all' ? 'is-disabled' : ''}`}
            >
              <input
                type="checkbox"
                checked={feedImageSettings.aiCreativeStrictSize && feedImageSettings.aiCreativeSize !== 'all'}
                disabled={feedImageSettings.aiCreativeSize === 'all'}
                onChange={(e) => setFeedImageSettings((prev) => ({ ...prev, aiCreativeStrictSize: e.target.checked }))}
              />
              <div className="facebook-ads-admin__card-content">
                <span className="facebook-ads-admin__card-label">Strict Size Only</span>
                <span className="facebook-ads-admin__card-sublabel">
                  {feedImageSettings.aiCreativeSize === 'all'
                    ? 'Select a specific ratio above to enable strict filtering'
                    : 'Do not fallback: only include creatives matching this exact size'}
                </span>
              </div>
            </label>
          </div>
        </div>

        <div className="facebook-ads-admin__settings-footer">
          <div className="facebook-ads-admin__notice-box">
            💡 <strong>Note:</strong> Changes take effect immediately when feeds are refreshed. Click <strong>Manual Refresh</strong> on the feeds below to rebuild them immediately with these settings.
          </div>
          {feedImageSettings.updatedAt && (
            <span style={{ fontSize: '12px', color: '#6b7280' }}>
              Last updated: {formatDateTime(feedImageSettings.updatedAt)} {feedImageSettings.updatedBy ? `by ${feedImageSettings.updatedBy}` : ''}
            </span>
          )}
        </div>
      </section>

      <section className="facebook-ads-admin__panel">
        <div className="facebook-ads-admin__panel-header">
          <h2>Complete Catalog Feed (Online Only)</h2>
          <button
            type="button"
            className="facebook-ads-admin__refresh"
            onClick={() => refreshSingleFeed('complete')}
            disabled={feedRefreshing.complete}
          >
            {feedRefreshing.complete ? 'Refreshing...' : 'Manual Refresh'}
          </button>
        </div>
        {feedRefreshing.complete && <div className="facebook-ads-admin__progress" aria-hidden="true" />}
        <label className="facebook-ads-admin__field">
          Feed URL
          <input type="text" readOnly value={withRefreshParam(completeCatalogPreview.feedUrl, manualRefreshNonceByFeed.complete)} />
        </label>
        <p className="facebook-ads-admin__feed-meta">
          Token required: <strong>{completeCatalogPreview.tokenRequired ? 'Yes' : 'No'}</strong> | Last updated: <strong>{formatDateTime(completeCatalogPreview.latestUpdatedAt)}</strong>
        </p>
      </section>

      <section className="facebook-ads-admin__panel">
        <div className="facebook-ads-admin__panel-header">
          <h2>Google Ads Product Feed</h2>
          <button
            type="button"
            className="facebook-ads-admin__refresh"
            onClick={() => refreshSingleFeed('google')}
            disabled={feedRefreshing.google}
          >
            {feedRefreshing.google ? 'Refreshing...' : 'Manual Refresh'}
          </button>
        </div>
        {feedRefreshing.google && <div className="facebook-ads-admin__progress" aria-hidden="true" />}
        <label className="facebook-ads-admin__field">
          Feed URL
          <input type="text" readOnly value={withRefreshParam(googleFeedPreview.feedUrl, manualRefreshNonceByFeed.google)} />
        </label>
        <p className="facebook-ads-admin__feed-meta">
          Token required: <strong>{googleFeedPreview.tokenRequired ? 'Yes' : 'No'}</strong> | Last updated: <strong>{formatDateTime(googleFeedPreview.latestUpdatedAt)}</strong>
        </p>
        <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <a
            href="/api/v1/google-ads/campaign-template.csv"
            download="google_ads_pmax_campaign_template.csv"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              backgroundColor: '#1a73e8',
              color: '#fff',
              borderRadius: '6px',
              textDecoration: 'none',
              fontSize: '13px',
              fontWeight: '600',
            }}
          >
            📥 Download 1-Click Google Ads Campaign CSV (PMax / Custom Labels)
          </a>
          <span style={{ fontSize: '12px', color: '#666' }}>
            Pre-configured with Saree, Dupatta, Dress Material, Kurti & Bridal Wear ad copy & custom label filters.
          </span>
        </div>
      </section>

      <section className="facebook-ads-admin__panel">
        <div className="facebook-ads-admin__panel-header">
          <h2>Google Reviews Feed (Published Only)</h2>
          <button
            type="button"
            className="facebook-ads-admin__refresh"
            onClick={() => refreshSingleFeed('googleReviews')}
            disabled={feedRefreshing.googleReviews}
          >
            {feedRefreshing.googleReviews ? 'Refreshing...' : 'Manual Refresh'}
          </button>
        </div>
        {feedRefreshing.googleReviews && <div className="facebook-ads-admin__progress" aria-hidden="true" />}
        <label className="facebook-ads-admin__field">
          Feed URL
          <input type="text" readOnly value={withRefreshParam(googleReviewFeedPreview.feedUrl, manualRefreshNonceByFeed.googleReviews)} />
        </label>
        <p className="facebook-ads-admin__feed-meta">
          Token required: <strong>{googleReviewFeedPreview.tokenRequired ? 'Yes' : 'No'}</strong> | Last updated: <strong>{formatDateTime(googleReviewFeedPreview.latestUpdatedAt)}</strong>
        </p>
      </section>

      <section className="facebook-ads-admin__panel">
        <div className="facebook-ads-admin__panel-header">
          <h2>Generate Single Creative</h2>
        </div>
        <form className="facebook-ads-admin__form-grid" onSubmit={handleGenerateOne}>
          <label className="facebook-ads-admin__field">
            Lookbook Entry ID (optional)
            <input
              type="text"
              value={generateForm.entryId}
              onChange={(event) => setGenerateForm((prev) => ({ ...prev, entryId: event.target.value }))}
              placeholder="entry id"
            />
          </label>
          <label className="facebook-ads-admin__field">
            Shopify Product ID (optional)
            <input
              type="text"
              value={generateForm.shopifyProductId}
              onChange={(event) => setGenerateForm((prev) => ({ ...prev, shopifyProductId: event.target.value }))}
              placeholder="e.g. 1234567890"
            />
          </label>
          <label className="facebook-ads-admin__field">
            Template
            <input type="text" value="split_70_30 (55/45)" readOnly />
          </label>
          <label className="facebook-ads-admin__field">
            Size
            <select
              value={generateForm.sizePreset}
              onChange={(event) => setGenerateForm((prev) => ({ ...prev, sizePreset: event.target.value }))}
            >
              <option value="1:1">1:1 (1440x1440)</option>
              <option value="4:5">4:5 (1440x1800)</option>
              <option value="9:16">9:16 (1440x2560)</option>
            </select>
          </label>
          <div className="facebook-ads-admin__actions">
            <button type="submit" disabled={busyGenerate}>{busyGenerate ? 'Generating...' : 'Generate Creative'}</button>
          </div>
        </form>
      </section>

      <section className="facebook-ads-admin__panel">
        <div className="facebook-ads-admin__panel-header">
          <h2>Shopify URL Backfill</h2>
        </div>
        <p className="facebook-ads-admin__feed-meta">
          Uploads missing creative images to Shopify CDN for feed URL optimization.
          {' '}Pending: <strong>{shopifyBackfillStatus.pendingCount}</strong> / Ready: <strong>{shopifyBackfillStatus.readyCount}</strong>
        </p>
        <p className="facebook-ads-admin__feed-meta">
          Auto run (every 10 min): <strong>{shopifyBackfillStatus.autoEnabled ? 'Running' : 'Paused'}</strong> | Batch per run: <strong>{shopifyBackfillStatus.batchLimit}</strong>
        </p>
        <div className="facebook-ads-admin__actions">
          <button type="button" onClick={handleBackfillShopifyImages} disabled={busyShopifyBackfill}>
            {busyShopifyBackfill ? 'Running...' : 'Run Backfill'}
          </button>
          <button type="button" className="secondary" onClick={handleToggleShopifyBackfillAuto} disabled={busyShopifyBackfillToggle}>
            {busyShopifyBackfillToggle ? 'Updating...' : (shopifyBackfillStatus.autoEnabled ? 'Pause Auto Run' : 'Start Auto Run')}
          </button>
        </div>
      </section>

      <section className="facebook-ads-admin__panel">
        <div className="facebook-ads-admin__panel-header">
          <h2>Batch Generate</h2>
        </div>
        <p className="facebook-ads-admin__feed-meta">
          Pending: <strong>{autoGenerateStatus.pendingCount}</strong>
        </p>
        <p className="facebook-ads-admin__feed-meta">
          Auto run (every 10 min): <strong>{autoGenerateStatus.autoEnabled ? 'Running' : 'Paused'}</strong> | Batch per run: <strong>{autoGenerateStatus.batchLimit}</strong>
        </p>
        <div className="facebook-ads-admin__actions">
          <button type="button" className="secondary" onClick={handleToggleAutoGenerate} disabled={busyAutoGenerateToggle}>
            {busyAutoGenerateToggle ? 'Updating...' : (autoGenerateStatus.autoEnabled ? 'Pause Auto Generate' : 'Start Auto Generate')}
          </button>
        </div>
        <form className="facebook-ads-admin__form-grid" onSubmit={handleGenerateBatch}>
          <label className="facebook-ads-admin__field">
            Limit
            <input
              type="number"
              min="1"
              max="200"
              value={batchForm.limit}
              onChange={(event) => setBatchForm((prev) => ({ ...prev, limit: event.target.value }))}
            />
          </label>
          <label className="facebook-ads-admin__field">
            Source Status
            <select
              value={batchForm.status}
              onChange={(event) => setBatchForm((prev) => ({ ...prev, status: event.target.value }))}
            >
              <option value="published">Published</option>
              <option value="generated">Generated</option>
              <option value="approved">Approved</option>
              <option value="">Any</option>
            </select>
          </label>
          <label className="facebook-ads-admin__field">
            Template
            <input type="text" value="split_70_30 (55/45)" readOnly />
          </label>
          <label className="facebook-ads-admin__field">
            Sizes
            <input type="text" value="1:1, 4:5, 9:16" readOnly />
          </label>
          <div className="facebook-ads-admin__actions">
            <button type="submit" disabled={busyBatch}>{busyBatch ? 'Running...' : 'Run Batch'}</button>
          </div>
        </form>
      </section>

      <section className="facebook-ads-admin__panel facebook-ads-admin__panel--creatives">
        <div
          className="facebook-ads-admin__panel-header facebook-ads-admin__accordion-header"
          onClick={handleToggleCreativesAccordion}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleToggleCreativesAccordion();
            }
          }}
        >
          <div className="facebook-ads-admin__accordion-title">
            <span
              className={`facebook-ads-admin__accordion-chevron ${isCreativesOpen ? 'is-open' : ''}`}
              aria-hidden="true"
            >
              ▶
            </span>
            <h2>Recent Creatives</h2>
            {creativesLoadedOnce && creativePagination.totalCount > 0 ? (
              <span className="facebook-ads-admin__accordion-badge">
                {creativePagination.totalCount} total
              </span>
            ) : null}
            {!isCreativesOpen && (
              <span className="facebook-ads-admin__accordion-hint">
                (Click to open &amp; load)
              </span>
            )}
          </div>
          <div className="facebook-ads-admin__accordion-controls" onClick={(e) => e.stopPropagation()}>
            {isCreativesOpen && (
              <div className="facebook-ads-admin__pagination">
                <button
                  type="button"
                  className="secondary"
                  onClick={() => handleCreativePageChange(Math.max(1, creativePagination.page - 1))}
                  disabled={creativesLoading || creativePagination.page <= 1}
                >
                  Prev
                </button>
                <span>Page {creativePagination.page} / {creativePagination.totalPages}</span>
                <button
                  type="button"
                  className="secondary"
                  onClick={() => handleCreativePageChange(Math.min(creativePagination.totalPages, creativePagination.page + 1))}
                  disabled={creativesLoading || creativePagination.page >= creativePagination.totalPages}
                >
                  Next
                </button>
                <button
                  type="button"
                  className="secondary"
                  title="Refresh Creatives"
                  onClick={() => fetchCreatives(creativePage, creativeSkuSearch)}
                  disabled={creativesLoading}
                >
                  {creativesLoading ? '...' : '↻'}
                </button>
              </div>
            )}
            <button
              type="button"
              className="secondary facebook-ads-admin__accordion-toggle-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleToggleCreativesAccordion();
              }}
            >
              {isCreativesOpen ? 'Collapse' : 'Expand'}
            </button>
          </div>
        </div>

        {isCreativesOpen && (
          <div className="facebook-ads-admin__accordion-body">
            <div className="facebook-ads-admin__search-row">
              <label className="facebook-ads-admin__field">
                Search by SKU
                <input
                  type="text"
                  value={creativeSkuSearchInput}
                  onChange={(event) => setCreativeSkuSearchInput(event.target.value)}
                  placeholder="Enter exact SKU"
                />
              </label>
              <div className="facebook-ads-admin__actions">
                <button type="button" onClick={handleApplyCreativeSkuSearch} disabled={creativesLoading}>Search</button>
                <button type="button" className="secondary" onClick={handleClearCreativeSkuSearch} disabled={creativesLoading}>Clear</button>
              </div>
            </div>
            {creativesLoading && <div className="facebook-ads-admin__empty">Loading creatives...</div>}
            {!creativesLoading && creatives.length === 0 && <div className="facebook-ads-admin__empty">No creatives generated yet.</div>}
            {!creativesLoading && creatives.length > 0 && (
              <div className="facebook-ads-admin__cards">
                {creatives.map((creative) => {
                  const outputImage = resolveMediaUrl(creative.outputImageUrl);
                  const lookbookImage = resolveMediaUrl(creative.lookbookImageUrl);
                  const fabricImage = resolveMediaUrl(creative.fabricImageUrl);
                  return (
                    <article key={creative.id} className="facebook-ads-admin__card">
                      <div className="facebook-ads-admin__card-image-wrap">
                        {outputImage ? (
                          <a href={outputImage} target="_blank" rel="noreferrer">
                            <img src={outputImage} alt={`Facebook ad creative ${creative.id}`} />
                          </a>
                        ) : (
                          <div className="facebook-ads-admin__image-placeholder">No image</div>
                        )}
                      </div>
                      <div className="facebook-ads-admin__card-meta">
                        <div><strong>Product:</strong> {creative.shopifyProductId || '-'}</div>
                        <div><strong>Template:</strong> {creative.template || '-'} | <strong>Size:</strong> {creative.sizePreset || '-'}</div>
                        <div><strong>Status:</strong> {creative.status || '-'}</div>
                        <div><strong>Updated:</strong> {formatDateTime(creative.updatedAt)}</div>
                        <div className="facebook-ads-admin__card-actions">
                          <button
                            type="button"
                            className="secondary"
                            onClick={() => handleRegenerateCreative(creative)}
                            disabled={processingCreativeId === creative.id}
                          >
                            {processingCreativeId === creative.id && processingCreativeAction === 'regenerate' ? 'Regenerating...' : 'Regenerate'}
                          </button>
                          <button
                            type="button"
                            className="danger"
                            onClick={() => handleDeleteCreative(creative)}
                            disabled={processingCreativeId === creative.id}
                          >
                            {processingCreativeId === creative.id && processingCreativeAction === 'delete' ? 'Deleting...' : 'Delete'}
                          </button>
                        </div>
                        <div className="facebook-ads-admin__links">
                          {outputImage && <a href={outputImage} target="_blank" rel="noreferrer">Creative</a>}
                          {lookbookImage && <a href={lookbookImage} target="_blank" rel="noreferrer">Lookbook</a>}
                          {fabricImage && <a href={fabricImage} target="_blank" rel="noreferrer">Fabric</a>}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

export default FacebookAdsPage;
